import { Investment, InvestmentResults } from '../types';
import { formatCurrency, convertFromMXN } from './formatters';
import { getCertificatePhasesSummary } from './calculations/certificateEvolution';
import { getDetailedCertificateEvolution } from './calculations/certificateEvolution';

export function generateFullReport(investment: Investment, results: InvestmentResults, returnContent = false): string | void {
  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString('es-MX', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Get detailed certificate evolution and phases
  const certificateEvolution = getDetailedCertificateEvolution(investment);
  const phases = getCertificatePhasesSummary(certificateEvolution, investment.years);

  const htmlContent = `
    <!DOCTYPE html>
    <html lang="es">
    <head>
      <meta charset="UTF-8">
      <title>Reporte Completo de Inversión - Cosecha Capital</title>
      <style>
        :root {
          --primary: #16a34a;
          --primary-light: #22c55e;
          --secondary: #0284c7;
          --gray-100: #f3f4f6;
          --gray-200: #e5e7eb;
          --gray-700: #374151;
          --gray-800: #1f2937;
        }
        
        body {
          font-family: system-ui, -apple-system, sans-serif;
          line-height: 1.6;
          max-width: 1200px;
          margin: 0 auto;
          padding: 40px 20px;
          color: var(--gray-800);
          background: #ffffff;
        }
        
        .header {
          text-align: center;
          margin-bottom: 60px;
          padding-bottom: 30px;
          border-bottom: 3px solid var(--primary);
        }
        
        .header h1 {
          color: var(--primary);
          font-size: 2.5em;
          margin-bottom: 10px;
        }
        
        .section {
          margin-bottom: 40px;
          padding: 30px;
          background: var(--gray-100);
          border-radius: 12px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .section-title {
          color: var(--primary);
          font-size: 1.5em;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid var(--primary-light);
        }
        
        .grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 20px;
          margin-bottom: 20px;
        }
        
        .stat-card {
          background: white;
          padding: 20px;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
          background: white;
          border-radius: 8px;
          overflow: hidden;
        }
        
        th, td {
          padding: 12px 16px;
          text-align: left;
          border-bottom: 1px solid var(--gray-200);
        }
        
        th {
          background: var(--primary);
          color: white;
          font-weight: 500;
        }
        
        tr:nth-child(even) {
          background: var(--gray-100);
        }
        
        .footer {
          text-align: center;
          margin-top: 60px;
          padding-top: 20px;
          border-top: 2px solid var(--gray-200);
          color: var(--gray-700);
        }
        
        .comparison-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
        }
        
        .comparison-card {
          background: white;
          padding: 20px;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .multiplier-highlight {
          background: linear-gradient(135deg, var(--primary), var(--primary-light));
          color: white;
          padding: 20px;
          border-radius: 12px;
          text-align: center;
          margin: 20px 0;
        }
        
        .highlight {
          background: #dcfce7 !important;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>Reporte Completo de Inversión</h1>
        <h2>Cosecha Capital</h2>
        <p>Generado el ${formattedDate}</p>
      </div>
      
      <div class="section">
        <h2 class="section-title">Resumen Ejecutivo</h2>
        <div class="grid">
          <div class="stat-card">
            <h3>Inversión Inicial</h3>
            <p>${formatCurrency(convertFromMXN(investment.initialCertificates * investment.certificateBasePrice, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}</p>
            <p><small>${investment.initialCertificates} certificados × ${formatCurrency(convertFromMXN(investment.certificateBasePrice, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}</small></p>
          </div>
          <div class="stat-card">
            <h3>Patrimonio Final</h3>
            <p>${formatCurrency(convertFromMXN(results.finalPatrimony, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}</p>
            <p><small>Al año ${investment.years}</small></p>
          </div>
          <div class="stat-card">
            <h3>Ingreso Mensual Final</h3>
            <p>${formatCurrency(convertFromMXN(results.finalMonthlyIncome, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}</p>
            <p><small>Ingreso pasivo proyectado</small></p>
          </div>
          <div class="stat-card">
            <h3>Multiplicador de Capital</h3>
            <p>${results.capitalMultiplier.toFixed(1)}x</p>
            <p><small>Crecimiento de la inversión</small></p>
          </div>
        </div>
      </div>

      <div class="section">
        <h2 class="section-title">Parámetros de Inversión</h2>
        <div class="grid">
          <div class="stat-card">
            <h3>Certificados Iniciales</h3>
            <p>${investment.initialCertificates}</p>
          </div>
          <div class="stat-card">
            <h3>Precio Base del Certificado</h3>
            <p>${formatCurrency(convertFromMXN(investment.certificateBasePrice, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}</p>
          </div>
          <div class="stat-card">
            <h3>Horizonte de Inversión</h3>
            <p>${investment.years} años</p>
          </div>
          <div class="stat-card">
            <h3>Producción por Hectárea</h3>
            <p>${investment.averageProductionPerHectare.toLocaleString()} kg/ha</p>
          </div>
          <div class="stat-card">
            <h3>Precio por Kg</h3>
            <p>$${investment.averageSalePricePerKg} MXN/kg</p>
          </div>
          <div class="stat-card">
            <h3>Utilidad Anual Calculada</h3>
            <p>${((investment.averageProductionPerHectare * investment.averageSalePricePerKg * 0.1 * 0.65 / investment.certificateBasePrice) * 100).toFixed(1)}%</p>
          </div>
        </div>
      </div>

      <div class="section">
        <h2 class="section-title">Resultados Finales</h2>
        <div class="grid">
          <div class="stat-card">
            <h3>Certificados Totales</h3>
            <p>${results.certificatesSummary.totalCertificates}</p>
            <p><small>Iniciales: ${results.certificatesSummary.initialCertificates} | Por reinversión: +${results.certificatesSummary.fromReinvestment}</small></p>
          </div>
          <div class="stat-card">
            <h3>Certificados por Fase</h3>
            <p>Produciendo: ${phases.producing}</p>
            <p>En maduración: ${phases.waiting}</p>
            <p>Reservados: ${phases.reserved}</p>
          </div>
          <div class="stat-card">
            <h3>CAGR</h3>
            <p>${(results.cagr * 100).toFixed(1)}%</p>
            <p><small>Tasa de crecimiento anual compuesta</small></p>
          </div>
          <div class="stat-card">
            <h3>TIR</h3>
            <p>${(results.irr * 100).toFixed(1)}%</p>
            <p><small>Tasa interna de retorno</small></p>
          </div>
        </div>
      </div>

      <div class="section">
        <h2 class="section-title">Evolución Anual</h2>
        <table>
          <thead>
            <tr>
              <th>Año</th>
              <th>Certificados Totales</th>
              <th>Patrimonio</th>
              <th>Ingreso Anual</th>
              <th>Utilidad Generada</th>
              <th>Fondo Reinversión</th>
            </tr>
          </thead>
          <tbody>
            ${results.yearlyData.map(data => `
              <tr ${data.year === 5 ? 'class="highlight"' : ''}>
                <td>${data.year}</td>
                <td>${certificateEvolution[data.year - 1]?.totalCertificates || investment.initialCertificates}</td>
                <td>${formatCurrency(convertFromMXN(data.citrusPatrimony, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}</td>
                <td>${formatCurrency(convertFromMXN(data.citrusIncome, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}</td>
                <td>${formatCurrency(convertFromMXN(certificateEvolution[data.year - 1]?.totalUtility || 0, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}</td>
                <td>${formatCurrency(convertFromMXN(certificateEvolution[data.year - 1]?.reinvestmentFund || 0, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>

      <div class="section">
        <h2 class="section-title">Comparativo con Otras Inversiones</h2>
        <div class="comparison-grid">
          <div class="comparison-card" style="border-left: 4px solid #16a34a;">
            <h3 style="color: #16a34a;">Cosecha Capital</h3>
            <p><strong>Patrimonio:</strong> ${formatCurrency(convertFromMXN(results.finalPatrimony, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}</p>
            <p><strong>Multiplicador:</strong> ${results.capitalMultiplier.toFixed(1)}x</p>
            <p><strong>Estrategia:</strong> Activos reales productivos</p>
          </div>
          
          <div class="comparison-card">
            <h3 style="color: #0284c7;">CETES</h3>
            <p><strong>Patrimonio:</strong> ${formatCurrency(convertFromMXN(results.cetesPatrimony, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}</p>
            <p><strong>Multiplicador:</strong> ${(results.cetesPatrimony / (investment.initialCertificates * investment.certificateBasePrice)).toFixed(1)}x</p>
            <p><strong>Rendimiento:</strong> ${investment.cetesRate}% anual</p>
          </div>
          
          <div class="comparison-card">
            <h3 style="color: #7c3aed;">Ahorro Tradicional</h3>
            <p><strong>Patrimonio:</strong> ${formatCurrency(convertFromMXN(results.savingsPatrimony, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}</p>
            <p><strong>Multiplicador:</strong> ${(results.savingsPatrimony / (investment.initialCertificates * investment.certificateBasePrice)).toFixed(1)}x</p>
            <p><strong>Rendimiento:</strong> ${investment.savingsRate}% anual</p>
          </div>
          
          <div class="comparison-card">
            <h3 style="color: #ea580c;">Bienes Raíces</h3>
            <p><strong>Patrimonio:</strong> ${formatCurrency(convertFromMXN(results.realEstatePatrimony, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}</p>
            <p><strong>Multiplicador:</strong> ${(results.realEstatePatrimony / (investment.initialCertificates * investment.certificateBasePrice)).toFixed(1)}x</p>
            <p><strong>Apreciación:</strong> ${investment.realEstateAppreciation}% + Renta ${investment.realEstateRent}%</p>
          </div>
        </div>
        
        <div class="multiplier-highlight">
          <h3>🏆 Ventaja de Cosecha Capital</h3>
          <p style="font-size: 1.3em; margin: 15px 0;">
            <strong>Cosecha Capital supera a la segunda mejor opción por ${Math.max(
              results.finalPatrimony / results.cetesPatrimony,
              results.finalPatrimony / results.savingsPatrimony,
              results.finalPatrimony / results.realEstatePatrimony
            ).toFixed(1)}x</strong>
          </p>
        </div>
      </div>

      <div class="section">
        <h2 class="section-title">Notas Importantes</h2>
        <ul>
          <li>Los primeros ${investment.isLongTermCalculator ? '6' : '5'} años corresponden al período de maduración de los certificados.</li>
          <li>A partir del año ${investment.isLongTermCalculator ? '6' : '5'} se comienzan a generar utilidades por la operación.</li>
          <li>El precio del certificado se aprecia un ${investment.appreciationRate}% anual durante los primeros 5 años.</li>
          ${investment.increaseLemonPrice ? 
            `<li>Se considera un incremento anual del ${investment.lemonPriceIncrease}% en el precio del limón a partir del año 6.</li>` : 
            '<li>No se considera incremento en el precio del limón.</li>'
          }
          <li>La utilidad anual se calcula con base en ${investment.averageProductionPerHectare.toLocaleString()} kg/ha × $${investment.averageSalePricePerKg}/kg × 0.1 × 0.65</li>
          ${investment.reinvestProfits ? 
            '<li><strong>En el mes 49, puedes elegir destinar las utilidades para adquirir nuevos certificados siguiendo este plan.</strong></li>' : 
            '<li>Las utilidades se reciben como ingreso mensual sin reinversión.</li>'
          }
          ${investment.partialCashOut ? 
            `<li>Se realiza un retiro parcial del ${investment.cashOutPercentage}% de las utilidades generadas.</li>` : 
            ''
          }
          ${investment.enablePaymentBoost ? 
            '<li>El boost de pago está activado para acelerar la adquisición de certificados.</li>' : 
            ''
          }
          <li>Todos los valores están expresados en ${investment.currencyFormat}.</li>
          ${investment.currencyFormat !== 'MXN' ? 
            `<li>Tipo de cambio utilizado: ${investment.currencyFormat === 'USD' ? investment.exchangeRate : investment.exchangeRateEUR} MXN por 1 ${investment.currencyFormat}.</li>` : 
            ''
          }
        </ul>
      </div>

      <div class="footer">
        <p>© ${new Date().getFullYear()} Cosecha Capital</p>
        <p>Este reporte es generado automáticamente y contiene proyecciones basadas en los parámetros especificados.</p>
        <p><strong>Aviso:</strong> Las proyecciones mostradas son estimaciones y no garantizan rendimientos futuros.</p>
      </div>
    </body>
    </html>
  `;

  if (returnContent) {
    return htmlContent;
  }

  // Create blob and download
  const blob = new Blob([htmlContent], { type: 'text/html' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Reporte_Completo_Cosecha_Capital_${new Date().toISOString().split('T')[0]}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}